<style scoped>
.filter-row {
  @apply flex flex-auto -mx-3 mb-2 mt-5;
}

.filter-row > div {
  @apply w-full md:w-1/6 px-3 mb-6 md:mb-0;
}

.filter-row > div > label {
  @apply block uppercase tracking-wide text-[rgb(102,102,102)] text-xs font-bold mt-2;
}

.filter-row > div > input,
.filter-row > div > select {
  @apply appearance-none block w-full border rounded py-2 px-2 mb-3 leading-tight focus:outline-none focus:bg-white;
}

table {
  @apply w-full mt-5 table-auto border-collapse text-[0.9em] shadow-md;
}

table thead tr {
  @apply bg-[rgb(224,224,224)] text-black text-left;
}

table th,
table td {
  @apply py-2 px-3;
}

table tbody tr {
  border-bottom: 1px solid #dddddd;
}

table tbody tr:nth-of-type(even) {
  background-color: #f3f3f3;
}

table tbody tr:last-of-type {
  border-bottom: 2px solid #dddddd;
}

.font-mono {
  font-family: monospace !important;
}

.filter-utility {
  width: 16px !important;
  margin: 36px 14px 0 0;
  @apply text-[1.2em];
}
</style>
<template>
  <div>
    <InfoCard>
      <div>
        Listen nedenfor inneholder en oversikt over hvordan du har betalt for tjenestene vi har levert til deg.<br />
        Den vil også inneholde informasjon om eventuelle utestående eller framtidige beløp.
        <br /><br />
        For å lese mer om hvordan tjenestene våre belastes,
        <a :href="supportLink" target="_blank">klikk her</a>.
      </div>
    </InfoCard>
    <BlockContainer class="mt-4 p-5">
      <form class="w-full max-w-lg">
        <div class="filter-row">
          <div class="filter-utility">
            <i class="budicon-filter mr-2"></i>
          </div>
          <div>
            <label>Periode fra og med</label>
            <select name="periodFrom" v-model="selectedPeriodFrom">
              <option value="">-- Alle --</option>
              <option v-for="option in periodOptions" :key="option.value" :value="option.value">
                {{ option.name }}
              </option>
            </select>
          </div>
          <div>
            <label>Periode til og med</label>
            <select name="periodTo" v-model="selectedPeriodTo">
              <option value="">-- Alle --</option>
              <option v-for="option in periodOptions" :key="option.value" :value="option.value">
                {{ option.name }}
              </option>
            </select>
          </div>
          <div>
            <label>Leieobjekt</label>
            <select name="rentalObjects" v-model="selectedRentalObject">
              <option value="">-- Alle --</option>
              <option v-for="option in rentalObjectOptions" :key="option.value" :value="option.value">
                {{ option.name }}
              </option>
            </select>
          </div>
          <div>
            <label>Leietaker</label>
            <input name="tenant" v-model="searchTenant" />
          </div>
          <div>
            <label>Produkt</label>
            <select name="product" v-model="selectedProduct">
              <option value="">-- Alle --</option>
              <option v-for="option in productOptions" :key="option.value" :value="option.value">
                {{ option.name }}
              </option>
            </select>
          </div>
          <div class="filter-utility">
            <button class="btn btn-outline-secondary btn-sm" @click="clearFilters" type="button" title="Tøm filter">
              <i class="budicon-eraser text-black"></i>
            </button>
          </div>
        </div>
      </form>
      <table>
        <thead>
          <tr>
            <th>Adresse</th>
            <th>Leietaker</th>
            <th>Product</th>
            <th>Periode</th>
            <th class="text-right">Fakturabeløp</th>
            <th>Dokumenttype</th>
            <th class="text-right">Fakturanummer</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in filteredFees" :key="row[10]">
            <td>{{ row[0] }}</td>
            <td>{{ row[1] }}</td>
            <td>{{ row[2] }}</td>
            <td>{{ isoDateToHuman(row[3]) }}</td>
            <td class="text-right font-mono"><Currency :amount="row[4]"></Currency></td>
            <td>{{ row[5] }}</td>
            <td class="text-right font-mono text-primary">
              <router-link
                :to="{
                  name: 'landlord_profile_invoice',
                  params: { invoiceId: row[10], landlordId: $attrs.landlordId },
                }"
              >
                {{ row[6] }}
              </router-link>
            </td>
          </tr>
        </tbody>
      </table>
    </BlockContainer>
  </div>
</template>
<script>
import { LandlordReportApi } from '@modules/husleieapi2';
import BlockContainer from '@/components/BlockContainer.vue';
import InfoCard from '@/components/InfoCard.vue';
import Currency from '@modules/husleie-framework/src/components/currency/Currency.vue';

const landlordReportApi = new LandlordReportApi();
const months = ['', 'jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'];

export default {
  name: 'Fees',
  components: {
    InfoCard,
    BlockContainer,
    Currency,
  },
  data() {
    return {
      filter: null,
      format: 'json',
      xlsxLoading: false,
      supportLink: 'https://husleie-no.zendesk.com/hc/no/articles/17704458907036-Betaling-for-tjenesten',
      feesData: {},
      dataFetched: false,
      selectedPeriodFrom: '',
      selectedPeriodTo: '',
      selectedRentalObject: '',
      searchTenant: '',
      selectedProduct: '',
    };
  },
  computed: {
    fees() {
      return this.feesData.data;
    },
    periodOptions() {
      if (!this.feesData.meta) {
        return [];
      }

      return this.feesData.meta.periods.map(period => ({
        name: this.isoDateToHuman(period),
        value: period,
      }));
    },
    rentalObjectOptions() {
      if (!this.feesData.meta) {
        return [];
      }

      return this.feesData.meta.rentalObjects.map(rentalObject => ({
        name: rentalObject.address,
        value: rentalObject.id,
      }));
    },
    productOptions() {
      if (!this.feesData.meta) {
        return [];
      }

      return this.feesData.meta.products.map(product => ({
        name: product,
        value: product,
      }));
    },
    filteredFees() {
      if (!this.feesData.meta) {
        return [];
      }

      return this.fees.filter(row => {
        let include = true;
        const periodFrom = this.selectedPeriodFrom || '';
        const periodTo = this.selectedPeriodTo || '';
        const rentalObjectId = this.selectedRentalObject || '';
        // Period filter
        if (row[3]) {
          if (periodFrom && row[3] < periodFrom) {
            include = false;
          }

          if (periodTo && row[3] > periodTo) {
            include = false;
          }
        }
        // Rental object filter
        if (rentalObjectId && row[9] && row[9] !== rentalObjectId) {
          include = false;
        }
        // Tenant match filter
        if (this.searchTenant && row[1]) {
          if (!row[1].toLowerCase().includes(this.searchTenant.toLowerCase())) {
            include = false;
          }
        }
        // Product filter
        if (this.selectedProduct && row[2] !== this.selectedProduct) {
          include = false;
        }
        // Return the conclusion if the row should be included
        return include;
      });
    },
  },
  watch: {
    feesData: {
      handler() {
        this.dataFetched = true;
      },
      deep: true,
    },
  },
  methods: {
    async fetchData() {
      try {
        const data = await landlordReportApi.getLandlordReports(
          [this.$attrs.landlordId],
          'fees',
          { query: { format: 'json' } },
          this.format === 'json' ? 'json' : 'blob'
        );
        this.feesData = data;
      } catch (error) {
        this.$toast('Kunne ikke produktkostnader.', 'error');
      }
    },
    isoDateToHuman(date) {
      const [year, month] = date.split('-');
      return `${months[parseInt(month, 10)]} ${year}`;
    },
    clearFilters() {
      this.selectedPeriodFrom = '';
      this.selectedPeriodTo = '';
      this.selectedRentalObject = '';
      this.searchTenant = '';
      this.selectedProduct = '';
    },
  },
  async mounted() {
    await this.fetchData();
  },
};
</script>
